/**
 * Générateur — Savoir que la probabilité d'un évènement est un nombre compris entre 0 et 1
 * Attendu ID : 679 | Niveau : 6ème | Domaine : Organisation et gestion de données et probabilités
 *
 * Templates exercices (niveau didactique) :
 *   - echelle_qualitative               (classer, tableau_vf, relier, ordonner)
 *   - droite_graduee_probabilite        (texte, qcm_unique)
 *   - issues_experience_aleatoire       (qcm_unique, texte)
 *   - proprietes_probabilite            (tableau_vf)
 *
 * Labels de référence :
 *   - Classer des événements sur une échelle qualitative
 *   - Placer des probabilités sur la droite 0–1
 *   - Identifier le hasard dans une situation
 *   - Identifier les issues d'une expérience aléatoire
 *   - Vrai/faux sur les propriétés d'une probabilité
 *
 * Variantes estimées : ~2000+ (pools larges + combinaisons)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeOrdonner, makeClasser,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 679;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ECHELLE   = 'Classer des événements sur une échelle qualitative';
const L_DROITE    = 'Placer des probabilités sur la droite 0–1';
const L_HASARD    = 'Identifier le hasard dans une situation';
const L_ISSUES    = 'Identifier les issues d\'une expérience aléatoire';
const L_PROP      = 'Vrai/faux sur les propriétés d\'une probabilité';

// ─── Pools d'événements classés par niveau qualitatif ───

const POOL_CERTAIN = [
    'Demain il y aura 24 heures',
    'Après le lundi, il y a le mardi',
    'Février a moins de 35 jours',
    'Si tu lances un dé à 6 faces, tu obtiens un nombre entre 1 et 6',
    'Le soleil se lèvera demain matin',
    'Un triangle a 3 côtés',
    'Dans un mois, il y a au moins 28 jours',
    'Si tu jettes une pièce, elle retombera sur Pile ou Face',
];

const POOL_TRES_PROBABLE = [
    'Il fait froid en France au mois de janvier',
    'Tu obtiens au moins une fois un 6 en lançant 20 fois un dé',
    // « Collège ouvert demain » retiré (revue 2026-08-02) : vrai ou faux selon
    // le jour réel (week-end, vacances) — remplacé par un événement objectif.
    'Obtenir un nombre entre 1 et 5 en lançant un dé à 6 faces',
    'Il pleuvra au moins un jour cet automne',
    'Au moins un élève de la classe sera absent cette semaine',
    'Tu obtiens au moins une fois Pile en lançant 10 fois une pièce',
];

const POOL_PROBABLE = [
    'Obtenir Face en lançant une pièce équilibrée',
    'Ton voisin a un animal de compagnie',
    'Obtenir un nombre pair avec un dé à 6 faces',
    // « Il pleut un samedi en mars » retiré (revue 2026-08-02) : dépend de la
    // région et de la météo — remplacé par un 50 % exact et universel.
    'Tirer une carte rouge dans un jeu de 32 cartes',
    'Obtenir un nombre supérieur à 2 avec un dé à 6 faces',
    'Une personne rencontrée au hasard est née un mardi, mercredi, jeudi ou vendredi',
];

const POOL_PEU_PROBABLE = [
    'Obtenir un 6 en lançant un dé à 6 faces',
    'Il neigera à Marseille cette semaine',
    'Obtenir 10 fois de suite Pile avec une pièce équilibrée',
    'Tirer l\'As de cœur dans un jeu de 32 cartes',
    'Gagner le gros lot à la loterie',
    'Croiser un sosie célèbre dans la rue aujourd\'hui',
];

const POOL_IMPOSSIBLE = [
    'Obtenir un 7 en lançant un dé à 6 faces',
    'Ta maman a 8 ans',
    'Ton voisin a un cheval ailé comme animal',
    'Il y a 32 jours en février',
    'Il pleut et il ne pleut pas au même moment',
    'Obtenir un nombre négatif avec un dé classique',
    'Un triangle a 4 côtés',
];

const NIVEAUX = ['Impossible', 'Peu probable', 'Probable', 'Très probable', 'Certain'];

// ─── Pools complémentaires pour l'échelle qualitative ───

const NIVEAU_NUM = {
    'Impossible': 0,
    'Peu probable': 1,
    'Probable': 2,
    'Très probable': 3,
    'Certain': 4,
};

const POOL_PAR_NIVEAU = {
    'Impossible': POOL_IMPOSSIBLE,
    'Peu probable': POOL_PEU_PROBABLE,
    'Probable': POOL_PROBABLE,
    'Très probable': POOL_TRES_PROBABLE,
    'Certain': POOL_CERTAIN,
};

// Évènements avec probabilité exacte connue (pour relier).
// Pool élargi (revue 2026-08-02, avant : 8 événements / 5 étiquettes → 28
// combinaisons possibles, 19 doublons par lot de 40) : 16 événements,
// 7 étiquettes, toutes les probabilités exactes et vérifiables.
const EVENTS_PROBA_CONNUE = [
    { evt: 'Obtenir un 7 avec un dé à 6 faces', label: 'impossible' },
    { evt: 'Obtenir un 8 avec un dé à 6 faces', label: 'impossible' },
    { evt: 'Obtenir 0 avec un dé à 6 faces', label: 'impossible' },
    { evt: 'Obtenir un nombre entre 1 et 6 avec un dé à 6 faces', label: 'certain' },
    { evt: 'Il fera jour demain à midi', label: 'certain' },
    { evt: 'Obtenir un nombre plus petit que 7 avec un dé à 6 faces', label: 'certain' },
    { evt: 'Obtenir Pile avec une pièce équilibrée', label: 'une chance sur deux' },
    { evt: 'Obtenir un nombre pair avec un dé équilibré', label: 'une chance sur deux' },
    { evt: 'Obtenir 1 ou 2 avec un dé équilibré', label: 'une chance sur trois' },
    { evt: 'Tirer une boule rouge dans un sac contenant une rouge, une bleue et une verte', label: 'une chance sur trois' },
    { evt: 'Tirer un cœur dans un jeu de 32 cartes', label: 'une chance sur quatre' },
    { evt: 'Obtenir Pile deux fois de suite avec une pièce équilibrée', label: 'une chance sur quatre' },
    { evt: 'Obtenir un 6 avec un dé équilibré', label: 'une chance sur six' },
    { evt: 'Obtenir un 1 avec un dé équilibré', label: 'une chance sur six' },
    { evt: 'Tirer le roi de cœur dans un jeu de 32 cartes', label: 'une chance sur trente-deux' },
    { evt: 'Tirer la dame de pique dans un jeu de 32 cartes', label: 'une chance sur trente-deux' },
];

// ─── Pools pour la droite graduée 0-1 ───

// Pool élargi (revue 2026-08-03, avant : 9 valeurs) : les graduations des
// dixièmes ajoutées à la figure rendent tous les dixièmes lisibles.
const PROBA_POINTS = [
    { val: '0', label: '0' },
    { val: '0,1', label: '0,1' },
    { val: '0,2', label: '0,2' },
    { val: '0,25', label: '0,25' },
    { val: '0,3', label: '0,3' },
    { val: '0,4', label: '0,4' },
    { val: '0,5', label: '0,5' },
    { val: '0,6', label: '0,6' },
    { val: '0,7', label: '0,7' },
    { val: '0,75', label: '0,75' },
    { val: '0,8', label: '0,8' },
    { val: '0,9', label: '0,9' },
    { val: '1', label: '1' },
];

// ─── Pools pour issues/hasard ───

const SITUATIONS_ALEATOIRES = [
    { desc: 'Tirer une carte au hasard dans un jeu de 32 cartes.', issues: 32, label: '32 cartes différentes' },
    { desc: 'Lancer un dé équilibré à 6 faces.', issues: 6, label: '6 nombres' },
    { desc: 'Lancer un dé équilibré à 10 faces numérotées de 1 à 10.', issues: 10, label: '10 nombres' },
    { desc: 'Lancer un dé équilibré à 12 faces numérotées de 1 à 12.', issues: 12, label: '12 nombres' },
    { desc: 'Lancer un dé équilibré à 20 faces.', issues: 20, label: '20 nombres' },
    { desc: 'Lancer une pièce équilibrée.', issues: 2, label: 'Pile ou Face' },
    { desc: 'Tirer une boule dans une urne contenant 3 rouges, 2 bleues et 5 vertes (en regardant seulement la couleur).', issues: 3, label: '3 couleurs' },
    { desc: 'Tirer une boule dans un sac contenant 4 rouges, 3 jaunes et 2 noires (en regardant seulement la couleur).', issues: 3, label: '3 couleurs' },
    { desc: 'Tirer une bille dans un sac contenant 5 billes roses et 4 billes oranges (en regardant seulement la couleur).', issues: 2, label: '2 couleurs' },
    { desc: 'Tourner une roue de loterie à 8 secteurs numérotés de 1 à 8.', issues: 8, label: '8 secteurs' },
    { desc: 'Tourner une roue de loterie à 12 secteurs numérotés de 1 à 12.', issues: 12, label: '12 secteurs' },
    { desc: 'Tourner une roue de loterie à 16 secteurs numérotés de 1 à 16.', issues: 16, label: '16 secteurs' },
    { desc: 'Tirer un jeton au hasard dans un sac contenant les 26 lettres de l\'alphabet.', issues: 26, label: '26 lettres' },
    { desc: 'Choisir au hasard un élève dans une classe de 25 élèves.', issues: 25, label: '25 élèves' },
    { desc: 'Tirer un billet au hasard dans une urne contenant 50 billets numérotés de 1 à 50.', issues: 50, label: '50 billets' },
    { desc: 'Tirer au hasard un jour du mois de janvier.', issues: 31, label: '31 jours' },
    { desc: 'Tirer au hasard un mois dans l\'année.', issues: 12, label: '12 mois' },
    { desc: 'Tirer au hasard une boule dans un sac contenant 6 boules bleues et 4 boules blanches (en regardant la couleur).', issues: 2, label: '2 couleurs' },
    // Ajouts revue 2026-08-03 : 6 situations, nombres d'issues inédits (4, 5, 7, 52, 100).
    { desc: 'Lancer un dé équilibré à 4 faces numérotées de 1 à 4.', issues: 4, label: '4 nombres' },
    { desc: 'Tourner une roue de loterie à 5 secteurs numérotés de 1 à 5.', issues: 5, label: '5 secteurs' },
    { desc: 'Tirer au hasard un jour de la semaine.', issues: 7, label: '7 jours' },
    { desc: 'Tirer une boule dans un sac contenant 2 rouges, 3 vertes, 1 bleue et 4 jaunes (en regardant seulement la couleur).', issues: 4, label: '4 couleurs' },
    { desc: 'Tirer une carte au hasard dans un jeu de 52 cartes.', issues: 52, label: '52 cartes différentes' },
    { desc: 'Tirer un billet au hasard dans une urne contenant 100 billets numérotés de 1 à 100.', issues: 100, label: '100 billets' },
];

// Pool élargi (revue 2026-08-03, avant : 6 entrées → C(6,3) = 20 trios de
// distracteurs seulement, un lot de 40 sur deux avec doublon).
const SITUATIONS_NON_ALEATOIRES = [
    'Calculer la somme 3 + 4.',
    'Lire l\'heure sur l\'horloge de la classe.',
    'Mesurer la longueur d\'un crayon avec une règle.',
    'Compter le nombre d\'élèves présents dans la classe.',
    'Lire la température affichée sur un thermomètre.',
    'Peser un objet sur une balance.',
    'Compter le nombre de côtés d\'un hexagone.',
    'Convertir 2 mètres en centimètres.',
    'Calculer le double de 15.',
    'Lire le nombre de pages d\'un livre.',
];

// ─── Pool V/F propriétés de la probabilité ───

const VF_VRAIES = [
    'Une probabilité est un nombre compris entre 0 et 1',
    'Un événement impossible a pour probabilité 0',
    'Un événement certain a pour probabilité 1',
    'Une probabilité peut s\'écrire sous forme de pourcentage',
    'Une probabilité peut s\'écrire sous forme de fraction',
    'Une probabilité peut valoir 0,25',
    'Une probabilité peut valoir exactement 1',
    'Les probabilités 1/4, 0,25 et 25 % sont égales',
];

const VF_FAUSSES = [
    'Une probabilité peut valoir 1,2',
    'Une probabilité peut être négative',
    'Un événement très probable se produit toujours',
    'Un événement peu probable est impossible',
    'Une probabilité peut valoir 2',
    'Un événement qui ne s\'est pas produit une fois est impossible',
    'Une probabilité peut valoir -0,5',
];

// ─── Générateurs ───

// Template 1 : echelle_qualitative

const genEchelleClasser = () => {
    // Tirer 2 ou 3 niveaux de manière aléatoire (échantillon varié)
    const nbNiveaux = randInt(3, 5);
    const niveauxChoisis = randomChoiceMultiple(NIVEAUX, nbNiveaux);
    const categories = niveauxChoisis.map(n => {
        const pool = POOL_PAR_NIVEAU[n];
        const nbItems = randInt(1, 2);
        return { name: n, items: randomChoiceMultiple(pool, nbItems) };
    });
    const payload = makeClasser(
        categories,
        '<p>Classe chacun des événements suivants dans la catégorie qui convient.</p>',
        opts(L_ECHELLE),
    );
    payload._meta.generatorKey = 'echelle_qualitative';
    return payload;
};

const genEchelleTableauVf = () => {
    // 5 affirmations mixtes (bonnes et mauvaises classifications).
    // Quota VARIABLE (revue 2026-08-02, avant : 3 Vrai / 2 Faux figé) : un
    // quota fixe permettait de déduire les dernières lignes des premières.
    const affirmations = [];
    const reponses = [];
    const niveauxShuffles = shuffle(NIVEAUX);
    const nbVraies = randInt(2, 4);

    // Affirmations vraies (classification fidèle au pool)
    niveauxShuffles.slice(0, nbVraies).forEach(n => {
        const evt = randomChoice(POOL_PAR_NIVEAU[n]);
        const suffixe = n === 'Certain' || n === 'Impossible' ? `est ${n.toLowerCase()}` : `est un événement ${n.toLowerCase()}`;
        affirmations.push(`${evt} ${suffixe}`);
        reponses.push(0); // Vrai
    });

    // Affirmations fausses (mauvaise classification), à DISTANCE ≥ 2 sur
    // l'échelle (revue 2026-08-02) : un croisement voisin (« le soleil se
    // lèvera demain est très probable » → Faux) est contestable, la frontière
    // entre niveaux voisins n'étant jamais définie à l'élève.
    niveauxShuffles.slice(nbVraies).forEach(n => {
        const evt = randomChoice(POOL_PAR_NIVEAU[n]);
        const distNiv = randomChoice(NIVEAUX.filter(x => Math.abs(NIVEAU_NUM[x] - NIVEAU_NUM[n]) >= 2));
        affirmations.push(`${evt} est ${distNiv === 'Certain' || distNiv === 'Impossible' ? distNiv.toLowerCase() : 'un événement ' + distNiv.toLowerCase()}`);
        reponses.push(1); // Faux
    });

    // Mélanger
    const indices = shuffle([0, 1, 2, 3, 4]);
    const lignesMelangees = indices.map(i => affirmations[i]);
    const reponsesMelangees = indices.map(i => reponses[i]);

    const payload = makeVraiFaux(
        lignesMelangees,
        reponsesMelangees,
        '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>{{element:0}}',
        opts(L_ECHELLE),
    );
    payload._meta.generatorKey = 'echelle_qualitative';
    return payload;
};

const genEchelleRelier = () => {
    // 4 événements à NIVEAUX distincts : deux événements du même niveau
    // donneraient deux cibles identiques à relier (appariement indécidable)
    const evenements = [];
    const niveauxVus = new Set();
    for (const p of shuffle([...EVENTS_PROBA_CONNUE])) {
        if (!niveauxVus.has(p.label)) {
            niveauxVus.add(p.label);
            evenements.push(p);
        }
        if (evenements.length === 4) break;
    }
    const pairs = evenements.map(p => ({
        source: p.evt,
        target: p.label,
    }));
    const payload = makeRelier(
        pairs,
        '<p>Relie chaque événement à son niveau de probabilité.</p>',
        opts(L_ECHELLE),
    );
    payload._meta.generatorKey = 'echelle_qualitative';
    return payload;
};

const genEchelleOrdonner = () => {
    // Tirer 1 événement dans chacun de 4 niveaux distincts, classer du moins probable au plus probable
    const niveauxChoisis = shuffle(NIVEAUX).slice(0, 4);
    // Trier par niveau croissant (impossible → certain)
    const tries = [...niveauxChoisis].sort((a, b) => NIVEAU_NUM[a] - NIVEAU_NUM[b]);
    const items = tries.map(n => randomChoice(POOL_PAR_NIVEAU[n]));

    const payload = makeOrdonner(
        items,
        '<p>Range les événements suivants du <strong>moins probable</strong> au <strong>plus probable</strong>.</p>',
        opts(L_ECHELLE),
    );
    payload._meta.generatorKey = 'echelle_qualitative';
    return payload;
};

// Template 2 : droite_graduee_probabilite

const COULEURS_POINT = ['#E24B4A', '#378ADD', '#1D9E75', '#8B5CF6', '#EF9F27'];

const svgDroiteGraduee = (x, label, color) => {
    // x est la position en px entre 20 et 400.
    // Graduations des dixièmes (revue 2026-08-03) : sans elles, lire 0,3 ou
    // distinguer 0,25 de 0,3 était impossible (3 repères seulement sur la droite).
    let ticksDixiemes = '';
    for (let k = 1; k < 10; k++) {
        if (k === 5) continue; // 0,5 a déjà sa graduation principale
        const tx = 20 + k * 38;
        ticksDixiemes += `<line x1="${tx}" y1="47" x2="${tx}" y2="53" stroke="#64748b" stroke-width="1"/>`;
    }
    return `<svg viewBox="0 0 420 80" xmlns="http://www.w3.org/2000/svg" style="max-width:420px;display:block;margin:8px auto">
<line x1="20" y1="50" x2="400" y2="50" stroke="#1e293b" stroke-width="2"/>
${ticksDixiemes}
<polygon points="400,50 390,45 390,55" fill="#1e293b"/>
<line x1="20" y1="45" x2="20" y2="55" stroke="#1e293b" stroke-width="2"/>
<line x1="210" y1="45" x2="210" y2="55" stroke="#1e293b" stroke-width="2"/>
<line x1="400" y1="45" x2="400" y2="55" stroke="#1e293b" stroke-width="2"/>
<text x="20" y="73" text-anchor="middle" font-size="14" fill="#1e293b">0</text>
<text x="210" y="73" text-anchor="middle" font-size="14" fill="#1e293b">0,5</text>
<text x="400" y="73" text-anchor="middle" font-size="14" fill="#1e293b">1</text>
<circle cx="${x}" cy="50" r="6" fill="${color}"/>
<text x="${x}" y="35" text-anchor="middle" font-size="14" fill="${color}" font-weight="bold">${label}</text>
</svg>`;
};

const pxFromProba = (val) => {
    const num = parseFloat(val.replace(',', '.'));
    return 20 + num * (400 - 20);
};

const LETTRES_POINT = ['A', 'B', 'C', 'D', 'M', 'N', 'P', 'Q', 'R', 'S'];

const genDroiteTexte = () => {
    const p = randomChoice(PROBA_POINTS);
    const letter = randomChoice(LETTRES_POINT);
    const color = randomChoice(COULEURS_POINT);
    const px = pxFromProba(p.val);
    // « écriture décimale » explicite (revue 2026-08-03) : le moteur n'accepte
    // pas les fractions (« 1/4 »), le contrat doit être annoncé à l'élève.
    const texte = `<p>Sur la droite graduée de 0 à 1 ci-dessous, à quelle probabilité correspond le point marqué ${letter} ? Donne ta réponse en écriture décimale.</p>${svgDroiteGraduee(px, letter, color)}`;
    const payload = makeSaisie(p.val, texte, opts(L_DROITE));
    payload._meta.generatorKey = 'droite_graduee_probabilite';
    return payload;
};

const genDroiteQcm = () => {
    const p = randomChoice(PROBA_POINTS);
    const letter = randomChoice(LETTRES_POINT);
    const color = randomChoice(COULEURS_POINT);
    const px = pxFromProba(p.val);
    const bonne = p.val;
    const num = parseFloat(bonne.replace(',', '.'));
    const fmt = (x) => x.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
    const distCandidates = [];
    // ± un trait de graduation en priorité (revue 2026-08-03) : l'erreur de
    // lecture d'un dixième — et sans distracteur au-dessus de la bonne
    // réponse, « cocher le max » réussissait à 46 %.
    shuffle([num + 0.1, num - 0.1]).filter(v => v >= 0 && v <= 1)
        .forEach(v => distCandidates.push(fmt(v)));
    if (num > 0 && num < 1) distCandidates.push(fmt(1 - num));      // complément à 1
    if (num < 0.5) distCandidates.push(fmt(num * 2));               // double
    if (num > 0.25) distCandidates.push('0,25');
    distCandidates.push('0,5', '0,75', '0,1');
    const distUniques = [...new Set(distCandidates)].filter(d => d !== bonne).slice(0, 3);
    while (distUniques.length < 3) distUniques.push(String(randInt(1, 9) / 10).replace('.', ','));

    const choix = [bonne, ...distUniques.slice(0, 3)];
    const texte = `<p>Parmi les valeurs suivantes, laquelle correspond au point ${letter} placé sur la droite graduée ?</p>${svgDroiteGraduee(px, letter, color)}`;
    const payload = makeQcm(choix, bonne, texte, opts(L_DROITE));
    payload._meta.generatorKey = 'droite_graduee_probabilite';
    return payload;
};

// Template 3 : issues_experience_aleatoire

const genHasardQcm = () => {
    const vraie = randomChoice(SITUATIONS_ALEATOIRES).desc;
    const fausses = randomChoiceMultiple(SITUATIONS_NON_ALEATOIRES, 3);
    const choix = [vraie, ...fausses];
    const payload = makeQcm(
        choix, vraie,
        '<p>Parmi les situations suivantes, laquelle est une <strong>expérience aléatoire</strong> ?</p>',
        opts(L_HASARD),
    );
    payload._meta.generatorKey = 'issues_experience_aleatoire';
    return payload;
};

const PRENOMS_ISSUES = [
    { n: 'Léa', fem: true }, { n: 'Hugo', fem: false }, { n: 'Amina', fem: true },
    { n: 'Noa', fem: false }, { n: 'Mila', fem: true }, { n: 'Ethan', fem: false },
    { n: 'Alice', fem: true }, { n: 'Sacha', fem: false }, { n: 'Inès', fem: true },
    { n: 'Max', fem: false },
];

const FORMULATIONS_ISSUES = [
    (p, desc) => `<p>${p.fem ? p.n + ' réalise' : p.n + ' réalise'} l'expérience suivante : ${desc.charAt(0).toLowerCase() + desc.slice(1)}</p><p>Combien d'<strong>issues différentes</strong> cette expérience possède-t-elle ?</p>`,
    (p, desc) => `<p>${p.n} veut faire une expérience aléatoire : ${desc.charAt(0).toLowerCase() + desc.slice(1)}</p><p>Combien y a-t-il d'<strong>issues possibles</strong> ?</p>`,
    (p, desc) => `<p>On propose à ${p.n} l'expérience suivante : ${desc.charAt(0).toLowerCase() + desc.slice(1)}</p><p>Quel est le <strong>nombre d'issues possibles</strong> de cette expérience ?</p>`,
    (p, desc) => `<p>${desc}</p><p><strong>Combien d'issues différentes</strong> cette expérience possède-t-elle ?</p>`,
];

// Anti-répétition immédiate (revue 2026-08-03) : mémorise la dernière
// situation servie pour ne jamais poser deux fois de suite la même question
// dans une série générée d'affilée.
let derniereSituationIssues = null;

const genIssuesTexte = () => {
    let sit = randomChoice(SITUATIONS_ALEATOIRES);
    let garde = 0;
    while (sit.desc === derniereSituationIssues && garde < 10) {
        sit = randomChoice(SITUATIONS_ALEATOIRES);
        garde++;
    }
    derniereSituationIssues = sit.desc;
    const prenom = randomChoice(PRENOMS_ISSUES);
    const formulation = randomChoice(FORMULATIONS_ISSUES);
    const texte = formulation(prenom, sit.desc);
    const payload = makeSaisie(String(sit.issues), texte, opts(L_ISSUES));
    payload._meta.generatorKey = 'issues_experience_aleatoire';
    return payload;
};

// Template 4 : proprietes_probabilite

const genProprietesVf = () => {
    // randInt(2, 4) (revue 2026-08-03, avant : 2-3) : harmonisé avec le V/F
    // de l'échelle — un quota trop prévisible permet de déduire les dernières
    // lignes des premières.
    const nbVraies = randInt(2, 4);
    const nbFausses = 5 - nbVraies;
    const vraies = randomChoiceMultiple(VF_VRAIES, nbVraies);
    const fausses = randomChoiceMultiple(VF_FAUSSES, nbFausses);
    const lignes = [];
    const reponses = [];
    vraies.forEach(v => { lignes.push(v); reponses.push(0); });
    fausses.forEach(f => { lignes.push(f); reponses.push(1); });
    const indices = shuffle(lignes.map((_, i) => i));
    const lignesMel = indices.map(i => lignes[i]);
    const reponsesMel = indices.map(i => reponses[i]);

    const payload = makeVraiFaux(
        lignesMel,
        reponsesMel,
        '<p>Indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}',
        opts(L_PROP),
    );
    payload._meta.generatorKey = 'proprietes_probabilite';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    echelle_qualitative_classer:     genEchelleClasser,
    echelle_qualitative_tableau_vf:  genEchelleTableauVf,
    echelle_qualitative_relier:      genEchelleRelier,
    echelle_qualitative_ordonner:    genEchelleOrdonner,
    droite_graduee_probabilite_texte:      genDroiteTexte,
    droite_graduee_probabilite_qcm_unique: genDroiteQcm,
    issues_experience_aleatoire_qcm_unique: genHasardQcm,
    issues_experience_aleatoire_texte:      genIssuesTexte,
    proprietes_probabilite_tableau_vf: genProprietesVf,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    programme_construction: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'echelle_qualitative',         label: L_ECHELLE, formatsList: buildFormats('echelle_qualitative') },
    { id: 'droite_graduee_probabilite',  label: L_DROITE,  formatsList: buildFormats('droite_graduee_probabilite') },
    { id: 'issues_experience_aleatoire', label: L_ISSUES,  formatsList: buildFormats('issues_experience_aleatoire') },
    { id: 'proprietes_probabilite',      label: L_PROP,    formatsList: buildFormats('proprietes_probabilite') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    POOL_PAR_NIVEAU, NIVEAU_NUM, EVENTS_PROBA_CONNUE, PROBA_POINTS,
    SITUATIONS_ALEATOIRES, SITUATIONS_NON_ALEATOIRES,
    VF_VRAIES, VF_FAUSSES,
};
